import React, { useMemo } from 'react';
import {
  addParamsToHref,
  getProductIdFromProductCode,
  isMiddleMouseClick,
  linkClickOpensInNewTab,
} from '@noths/polaris-client-utils';
import type { ProductCardPayload } from '@noths/polaris-design-system-candidate-components';
import { ProductCard } from '@noths/polaris-design-system-candidate-components';

import { adaptProductToProductCardProps } from 'src/adapters/storeToUI/adaptProductToProductCardProps';
import { InListingCallout } from 'src/components/molecules/InListingCallout/InListingCallout';
import type { ProductListProps } from 'src/components/organisms/ProductList/types';
import { PRODUCT_CARD_GRID_ITEM_ID } from 'src/components/organisms/ProductPages/constants/elementIds';
import type { Product } from 'src/redux/products/types/Product';
import { PRODUCT_ID_DATA_ATTRIBUTE } from 'src/utils/scrollProductCardIntoView';
import * as styles from './ProductList.styles';

const INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING = 6;
/**
 * How many products the in-listings callout follows.
 *
 * It used to be pinned to grid row 8, which meant it followed 14 products on mobile and
 * 21 from tablet. A row number says nothing about how many products precede it once the
 * column count answers to the container, so this counts products instead and reads the
 * same at every width.
 */
const IN_LISTING_CALLOUT_AFTER_PRODUCT_COUNT = 12;
export const PRODUCT_CARD_GRID_ITEM_TEST_ID = 'PRODUCT_CARD_GRID_ITEM_TEST_ID';
/**
 * Currently responsible for rendering:
 *  - lists of products
 *  - lists of expandedProducts (if expanded search scenario)
 *  - expanded search messaging
 *
 * Should be responsible for rendering:
 *  - a list of products (agnostic about whether it's a full page, exact results, expanded search results)
 * @component
 */
export const ProductList = ({
  currency,
  expandedProducts,
  expandedResultsMessage,
  favouriteProductCodes = [],
  inListingsCallout,
  isSinglePartnerListing,
  onInListingsCalloutClick,
  onMediaNavigate,
  onProductClick,
  onProductVisible,
  page,
  products,
  providerAttributionToken,
  referredBy,
  showMultipleImages,
  updateFavourite,
  updatePageHistory,
}: ProductListProps) => {
  /* Converted to a Set for O(1) lookup, and to numbers so the comparison happens once
     per favourite rather than once per card: the store types a product code as a number
     and there are far more cards on a listing than a customer has favourites. */
  const favouriteProductCodesSet = useMemo(
    () => new Set(favouriteProductCodes.map(Number)),
    [favouriteProductCodes],
  );

  /* Adapting a product allocates a handful of objects, and a listing holds hundreds of
     products, so it is done once per product rather than once per render: a favourite
     being toggled re-renders this list but does not change what any card is made of.
     Keyed by the product object, which the store keeps stable, so both lists below read
     from the same map.

     `now` is fixed for the whole map, so every card dates its delivery from the same
     instant, and it moves on when the products do. A list left open across midnight
     dates from when it was adapted, which is what it already did anyway. */
  const productCardProps = useMemo(() => {
    const now = new Date();

    return new Map(
      [...(products ?? []), ...(expandedProducts ?? [])].map(
        (product) =>
          [
            product,
            adaptProductToProductCardProps(product, {
              currencyCode: currency,
              isSinglePartnerListing,
              now,
              showMultipleImages,
            }),
          ] as const,
      ),
    );
  }, [currency, expandedProducts, isSinglePartnerListing, products, showMultipleImages]);

  // Create tracking params
  const trackingParams = {
    ...(providerAttributionToken && { attributionToken: providerAttributionToken }),
    ...(referredBy && { referredBy }),
  };

  /* The card speaks the OOUX interface, which types the product code as a string,
     while the container callbacks below it still take the store's number. The
     conversion belongs here, at the boundary between the two. */
  const handleProductVisible = ({ product }: ProductCardPayload) => {
    onProductVisible({ productCode: Number(product.code) });
  };

  const handleFavouriteClick = (_evt: React.MouseEvent, { product }: ProductCardPayload) => {
    updateFavourite({ productCode: Number(product.code) });
  };

  const handleProductCardClick =
    (mousedown: boolean) =>
    (evt: React.MouseEvent<HTMLAnchorElement>, { product }: ProductCardPayload) => {
      const productCode = Number(product.code);
      const opensInNewTab = linkClickOpensInNewTab(evt) || isMiddleMouseClick(evt);

      if (mousedown && !isMiddleMouseClick(evt)) {
        return;
      }

      if (!opensInNewTab) {
        evt.preventDefault();
      } else {
        updatePageHistory({ page, productCode });
      }

      const hrefWithTrackingParams = addParamsToHref(evt.currentTarget.href, trackingParams);

      onProductClick({
        productUrl: hrefWithTrackingParams,
        destinationUrl: evt.currentTarget.href,
        productCode,
        linkOpensInSameWindow: !opensInNewTab,
      });
    };

  /* Built once rather than inside the map, so a list of 60 cards does not build 120
     closures every time it renders. */
  const handleProductClick = handleProductCardClick(false);
  const handleProductMouseDown = handleProductCardClick(true);

  const renderProducts = (products: Product[]) => {
    return products.map((product, index) => {
      // Every product rendered here came from one of the two lists the map was built from.
      const cardProps = productCardProps.get(product)!;

      return (
        <div
          css={styles.productCardGridItem}
          data-testid={PRODUCT_CARD_GRID_ITEM_TEST_ID}
          id={`${PRODUCT_CARD_GRID_ITEM_ID}-${index}`}
          key={`${product.code}`}
          {...{ [PRODUCT_ID_DATA_ATTRIBUTE]: getProductIdFromProductCode(product.code) }}
        >
          <ProductCard
            {...cardProps}
            favourite={{
              active: favouriteProductCodesSet.has(product.code),
              onClick: handleFavouriteClick,
            }}
            onMediaNavigate={onMediaNavigate}
            onProductClick={handleProductClick}
            onProductMouseDown={handleProductMouseDown}
            onVisible={handleProductVisible}
            priority={index < INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING}
          />
        </div>
      );
    });
  };

  /* Clamped, so a page holding fewer products than that still shows the callout, at
     the end of the list rather than not at all. */
  const withInListingCallout = (cards: JSX.Element[]) => {
    if (!inListingsCallout) {
      return cards;
    }

    const position = Math.min(IN_LISTING_CALLOUT_AFTER_PRODUCT_COUNT, cards.length);

    return [
      ...cards.slice(0, position),
      <aside css={styles.inListingCalloutWrapper} key="in-listing-callout">
        <InListingCallout
          ctaHref={inListingsCallout.ctaHref}
          ctaText={inListingsCallout.ctaText}
          heading={inListingsCallout.heading}
          imgSrc={inListingsCallout.imgUrl}
          onClick={onInListingsCalloutClick}
        />
      </aside>,
      ...cards.slice(position),
    ];
  };

  return (
    <div css={[styles.productGrid, styles.productListWrapper]}>
      {withInListingCallout(products ? renderProducts(products) : [])}
      {expandedResultsMessage && expandedResultsMessage}
      {expandedProducts && renderProducts(expandedProducts)}
    </div>
  );
};
